import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { LabelWithHint } from '@/components/help/LabelWithHint';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AITextarea } from '@/components/ui/ai-textarea';
import { Badge } from '@/components/ui/badge';
import { CreateActivityDialog } from '@/components/activities/CreateActivityDialog';
import type { MarketingActivity, ActivityStatus, ActivityType, PriorityLevel, User, Team } from '@/types';
import type { ApiResponse, PaginatedResponse } from '@/types';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import KanbanView from './KanbanView';
import ListView from './ListView';
import CalendarView from './CalendarView';
import { GanttView } from './GanttView';
import { ImportDialog } from '@/components/import/ImportDialog';
import {
  Plus,
  LayoutGrid,
  List,
  Calendar,
  GanttChart,
  Upload,
  ClipboardList,
  Clock3,
  CheckCircle2,
  RotateCcw,
} from 'lucide-react';
import { useHashFilters } from '@/hooks/useHashFilters';
import type { CreateActivityFormState } from '@/components/activities/CreateActivityDialog';
import { withReturnTo } from '@/lib/return-path';

export interface ActivityFilters {
  status: ActivityStatus | 'all';
  type: ActivityType | 'all';
  priority: PriorityLevel | 'all';
  team_id: string;
  assigned_to: string;
  date_from: string;
  date_to: string;
}

const STATUS_OPTIONS: { value: ActivityStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Alle Status' },
  { value: 'backlog', label: 'Backlog' },
  { value: 'todo', label: 'To-Do' },
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Erledigt' },
];

const TYPE_OPTIONS: { value: ActivityType | 'all'; label: string }[] = [
  { value: 'all', label: 'Alle Typen' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'linkedin_ads', label: 'LinkedIn Ads' },
  { value: 'print', label: 'Print' },
  { value: 'email', label: 'E-Mail' },
  { value: 'campaign', label: 'Kampagne' },
  { value: 'social', label: 'Social' },
  { value: 'event', label: 'Event' },
  { value: 'content', label: 'Content' },
  { value: 'seo', label: 'SEO' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'other', label: 'Sonstige' },
];

const PRIORITY_OPTIONS: { value: PriorityLevel | 'all'; label: string }[] = [
  { value: 'all', label: 'Alle Prioritäten' },
  { value: 'low', label: 'Niedrig' },
  { value: 'medium', label: 'Mittel' },
  { value: 'high', label: 'Hoch' },
  { value: 'critical', label: 'Kritisch' },
];

const defaultFilters: ActivityFilters = {
  status: 'all',
  type: 'all',
  priority: 'all',
  team_id: '',
  assigned_to: '',
  date_from: '',
  date_to: '',
};
const ACTIVITIES_HERO_IMAGE =
  'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?auto=format&fit=crop&w=1400&q=80';

export function ActivitiesPage({
  defaultView,
  onNavigate,
  listPath,
}: {
  defaultView?: 'kanban' | 'list' | 'calendar';
  onNavigate: (path: string) => void;
  /** Current list route; used so «Zurück» from an activity returns here. */
  listPath: '/activities' | '/activities/calendar';
}) {
  const [activities, setActivities] = useState<MarketingActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'kanban' | 'list' | 'calendar' | 'gantt'>(defaultView ?? 'kanban');
  const { filters, setFilters, setFilter } = useHashFilters({
    status: 'all',
    type: 'all',
    priority: 'all',
    team_id: '',
    assigned_to: '',
    date_from: '',
    date_to: '',
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [teamOptions, setTeamOptions] = useState<Team[]>([]);

  const openActivityDetail = (id: string) => {
    onNavigate(withReturnTo(`/activities/${id}`, listPath));
  };
  const [createSlotOverrides, setCreateSlotOverrides] = useState<Partial<CreateActivityFormState> | null>(null);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const pageSize = 100;
      let page = 1;
      const allActivities: MarketingActivity[] = [];
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.type !== 'all') params.set('type', filters.type);
      if (filters.priority !== 'all') params.set('priority', filters.priority);
      if (filters.team_id) params.set('team_id', filters.team_id);
      if (filters.assigned_to) params.set('assigned_to', filters.assigned_to);
      if (filters.date_from) params.set('start_date', filters.date_from);
      if (filters.date_to) params.set('end_date', filters.date_to);
      params.set('limit', String(pageSize));

      while (true) {
        params.set('page', String(page));
        const res = await api.get<PaginatedResponse<MarketingActivity>>(
          `/activities?sort_by=sort_order&order=asc&${params.toString()}`
        );
        const batch = res.data ?? [];
        allActivities.push(...batch);
        if (batch.length < pageSize) break;
        page += 1;
      }

      setActivities(allActivities);
    } catch {
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    if (activeView === 'kanban' && filters.status !== 'all') {
      setFilter('status', 'all');
    }
  }, [activeView, filters.status, setFilter]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [usersRes, teamsRes] = await Promise.all([
          api.get<ApiResponse<User[]>>('/users?limit=200&is_active=true'),
          api.get<ApiResponse<Team[]>>('/teams?limit=200'),
        ]);
        setUserOptions(Array.isArray(usersRes.data) ? usersRes.data : []);
        setTeamOptions(Array.isArray(teamsRes.data) ? teamsRes.data : []);
      } catch {
        setUserOptions([]);
        setTeamOptions([]);
      }
    };
    loadMeta();
  }, []);

  useEffect(() => {
    const shouldOpenCreate = window.localStorage.getItem('leftclick_open_create_activity') === '1';
    if (shouldOpenCreate) {
      setCreateDialogOpen(true);
      window.localStorage.removeItem('leftclick_open_create_activity');
    }
  }, []);

  const refreshActivities = () => loadActivities();
  const doneCount = activities.filter((item) => item.status === 'done').length;
  const inProgressCount = activities.filter((item) => item.status === 'in_progress' || item.status === 'review').length;

  const openCreateAtSlot = (date: string, time: string) => {
    setCreateSlotOverrides({
      title: '',
      description: `Termin geplant um ${time}`,
      start_date: date,
      end_date: date,
      type: 'other',
      status: 'backlog',
      priority: 'medium',
      budget_planned: '0',
      hours_planned: '0',
    });
    setCreateDialogOpen(true);
  };

  return (
    <div className="flex h-full flex-col p-6">
      <div className="relative mb-5 overflow-hidden rounded-2xl border border-border bg-card shadow-sm dark:bg-background">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.14] dark:opacity-[0.1]"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.96), rgba(255,255,255,0.78)), url(${ACTIVITIES_HERO_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative p-5 lg:p-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent-soft px-3 py-1 text-xs font-medium text-accent">
              <ClipboardList className="h-3.5 w-3.5" />
              Task Command Center
            </div>
            <h2 className="mt-3 text-xl font-semibold text-foreground">
              Aktivitäten klar priorisieren
            </h2>
            <p className="mt-1 text-sm text-muted">
              Behalte Status, Zeitfenster und Fortschritt in Kanban, Liste, Kalender oder Timeline im Blick.
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card/60">
                <p className="inline-flex items-center gap-1 text-xs text-muted"><ClipboardList className="h-3 w-3" /> Gesamt</p>
                <p className="text-lg font-semibold text-foreground">{activities.length}</p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card/60">
                <p className="inline-flex items-center gap-1 text-xs text-muted"><Clock3 className="h-3 w-3" /> In Arbeit</p>
                <p className="text-lg font-semibold text-foreground">{inProgressCount}</p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card/60">
                <p className="inline-flex items-center gap-1 text-xs text-muted"><CheckCircle2 className="h-3 w-3" /> Erledigt</p>
                <p className="text-lg font-semibold text-foreground">{doneCount}</p>
              </div>
            </div>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-3xl space-y-2">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Aktivitäten</h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
            <Upload className="h-4 w-4" />
            CSV Import
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Neue Aktivität
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-background/50 p-4 dark:bg-card/30">
        <div className="grid gap-1.5">
          <LabelWithHint
            label="Status"
            hint="Im Kanban-Modus wird der Status über die Spalten gesteuert — das Status-Dropdown ist dann deaktiviert."
          />
          <Select
            value={filters.status}
            onValueChange={(v) => setFilter('status', v as ActivityFilters['status'])}
            disabled={activeView === 'kanban'}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <LabelWithHint label="Typ" hint="z. B. Google Ads, E-Mail, Content — entspricht dem Kanal oder der Art der Massnahme." />
          <Select
            value={filters.type}
            onValueChange={(v) => setFilter('type', v as ActivityFilters['type'])}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <LabelWithHint label="Priorität" hint="Hilft bei der Sortierung; kritische Themen sollten höchste Priorität haben." />
          <Select
            value={filters.priority}
            onValueChange={(v) => setFilter('priority', v as ActivityFilters['priority'])}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <LabelWithHint label="Von" hint="Startdatum für die Aktivitätenliste (Start/Ende der Aktivität)." />
          <Input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilter('date_from', e.target.value)}
            className="w-[140px]"
          />
        </div>
        <div className="grid gap-1.5">
          <LabelWithHint label="Bis" hint="Enddatum des Filters; zusammen mit „Von“ den sichtbaren Zeitraum eingrenzen." />
          <Input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilter('date_to', e.target.value)}
            className="w-[140px]"
          />
        </div>
        {teamOptions.length > 0 && (
          <div className="grid gap-1.5">
            <LabelWithHint label="Team" hint="Nur Aktivitäten, die dieses Team zugeordnet haben." />
            <Select
              value={filters.team_id || 'all'}
              onValueChange={(v) => setFilter('team_id', v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Alle Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Teams</SelectItem>
                {teamOptions.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {userOptions.length > 0 && (
          <div className="grid gap-1.5">
            <LabelWithHint label="Zugewiesen an" hint="Filter auf die verantwortliche Person (Owner) der Aktivität." />
            <Select
              value={filters.assigned_to || 'all'}
              onValueChange={(v) => setFilter('assigned_to', v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Alle Personen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Personen</SelectItem>
                {userOptions.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" onClick={() => setFilters(defaultFilters)}>
              <RotateCcw className="h-4 w-4" />
              Filter zurücksetzen
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-sm">Setzt alle Filter auf Standard zurück (Ansicht bleibt).</TooltipContent>
        </Tooltip>
      </div>

      <Tabs value={activeView} onValueChange={(value) => setActiveView(value as 'kanban' | 'list' | 'calendar' | 'gantt')} className="flex-1">
        <TabsList className="mb-4">
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <TabsTrigger value="kanban" className="gap-1.5">
                <LayoutGrid className="h-4 w-4" />
                Kanban
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-sm">Spalten nach Status; Karten per Drag verschieben (wenn erlaubt).</TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <TabsTrigger value="list" className="gap-1.5">
                <List className="h-4 w-4" />
                Liste
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-sm">Tabellarische Übersicht mit Sortierung — gut für viele Einträge.</TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <TabsTrigger value="calendar" className="gap-1.5">
                <Calendar className="h-4 w-4" />
                Kalender
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-sm">Termin- und Fristenansicht; für Detailbearbeitung Aktivität öffnen.</TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <TabsTrigger value="gantt" className="gap-1.5">
                <GanttChart className="h-4 w-4" />
                Timeline
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-sm">Balkenplan über die Zeit — Abhängigkeiten und Überschneidungen erkennen.</TooltipContent>
          </Tooltip>
        </TabsList>
        <TabsContent value="kanban" className="mt-0 flex-1">
          <KanbanView
            activities={activities}
            loading={loading}
            onRefresh={refreshActivities}
            onActivityClick={openActivityDetail}
          />
        </TabsContent>
        <TabsContent value="list" className="mt-0 flex-1">
          <ListView
            activities={activities}
            loading={loading}
            onRefresh={refreshActivities}
            onActivityClick={openActivityDetail}
          />
        </TabsContent>
        <TabsContent value="calendar" className="mt-0 flex-1">
          <CalendarView
            activities={activities}
            loading={loading}
            onActivityClick={openActivityDetail}
            onCreateAtSlot={openCreateAtSlot}
          />
        </TabsContent>
        <TabsContent value="gantt" className="mt-0 flex-1">
          <GanttView
            activities={activities}
            loading={loading}
            onRefresh={refreshActivities}
            onActivityClick={openActivityDetail}
          />
        </TabsContent>
      </Tabs>

      <CreateActivityDialog
        open={createDialogOpen}
        onOpenChange={(o) => {
          setCreateDialogOpen(o);
          if (!o) setCreateSlotOverrides(null);
        }}
        onCreated={refreshActivities}
        initialOverrides={createSlotOverrides}
      />

      <ImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        entityType="activities"
        onSuccess={refreshActivities}
      />
    </div>
  );
}
